// Split VERBATIM from utils/datetime-comprehensive.js (ESM barrel split).
import { DateTime } from 'luxon';

export const BUSINESS_TIMEZONE = 'America/Chicago';

// Never set global defaults to avoid surprises in SSR/CSR
// Settings.defaultZone = null; // Explicitly avoid global defaults

/**
 * Parse ISO-8601 string with Z/offset and validate
 * @param {string} isoString - ISO-8601 string with timezone info
 * @returns {DateTime} - Validated DateTime in UTC
 * @throws {Error} - If invalid format or timezone
 */

export function parseUTC(isoString) {
  if (!isoString) {
    throw new Error('ISO string is required');
  }
  
  const dt = DateTime.fromISO(isoString, { zone: 'utc' });
  
  if (!dt.isValid) {
    throw new Error(`Invalid ISO string: ${dt.invalidReason} - ${dt.invalidExplanation}`);
  }
  
  return dt;
}

/**
 * Convert Chicago wall-time to UTC for storage
 * @param {Object|DateTime} input - DateTime object with Chicago time or DateTime object
 * @returns {DateTime|string} - DateTime in UTC or UTC ISO string
 */

export function chicagoToUTC(input) {
  if (!input) return null;
  
  try {
    let dt;
    
    // If input is a DateTime object, use it directly
    if (DateTime.isDateTime(input)) {
      dt = input;
    } else {
      // If input is an object, create DateTime from it
      dt = DateTime.fromObject(input, { zone: BUSINESS_TIMEZONE });
    }
    
    if (!dt.isValid) {
      throw new Error(`Invalid Chicago time: ${dt.invalidReason} - ${dt.invalidExplanation}`);
    }
    
    const utc = dt.toUTC();
    return utc.toISO();
  } catch (error) {
    console.error('Error converting Chicago time to UTC:', error);
    return null;
  }
}

/**
 * Convert UTC (from DB) to Chicago for business logic
 * @param {string} isoUtc - ISO string in UTC
 * @returns {DateTime} - DateTime in Chicago timezone
 */

export function utcToChicago(isoUtc) {
  return parseUTC(isoUtc).setZone(BUSINESS_TIMEZONE);
}

/**
 * Format DateTime for display in user's locale
 * @param {DateTime} dt - DateTime object
 * @param {Object} options - Luxon formatting options
 * @returns {string} - Formatted string
 */

export function nowUTC() {
  return DateTime.utc();
}

/**
 * Get current time in Chicago for business logic
 * @returns {DateTime} - Current time in Chicago
 */

export function nowChicago() {
  return DateTime.now().setZone(BUSINESS_TIMEZONE);
}

/**
 * Calculate the current week number based on start date, accounting for break periods
 * Week 1 ends at start_date + 1 week
 * Week 2 starts after Week 1 ends, and so on
 * Break periods are subtracted from elapsed time
 * @param {string|DateTime} startDate - Start date/time in UTC or DateTime object
 * @param {DateTime} [currentTime] - Optional current time (defaults to now)
 * @param {Array} [breakPeriods] - Optional array of break periods {start: DateTime, end: DateTime}
 * @returns {number} - Current week number (1-based), or 0 if start date hasn't passed
 */

export function getChicagoNow() {
  return nowChicago();
}

/**
 * Parse UTC timestamp and convert to Chicago (alias for utcToChicago)
 * @param {string} utcTimestamp - UTC timestamp from database
 * @returns {DateTime} - DateTime in Chicago timezone
 */
